import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { keycloak, initKeycloak, login, logout, startTokenRefreshLoop, stopTokenRefreshLoop } from "./keycloak";

export interface KeycloakUser {
  sub: string;
  username?: string;
  email?: string;
  name?: string;
  roles: string[];
}

export interface KeycloakContextValue {
  initialized: boolean;
  authenticated: boolean;
  user: KeycloakUser | undefined;
  initError: Error | undefined;
  login: () => void;
  logout: () => void;
}

const KeycloakContext = createContext<KeycloakContextValue | undefined>(undefined);

export function KeycloakProvider({ children }: { children: ReactNode }) {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [initError, setInitError] = useState<Error | undefined>();

  useEffect(() => {
    let mounted = true;
    initKeycloak()
      .then((auth) => {
        if (!mounted) return;
        setAuthenticated(auth);
        setInitialized(true);
        if (auth) startTokenRefreshLoop();
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        console.error("[keycloak] init failed", err);
        setInitError(err instanceof Error ? err : new Error(String(err)));
        setInitialized(true);
      });

    keycloak.onAuthSuccess = () => setAuthenticated(true);
    keycloak.onAuthLogout = () => setAuthenticated(false);
    keycloak.onAuthRefreshError = () => {
      setAuthenticated(false);
    };

    return () => {
      mounted = false;
      stopTokenRefreshLoop();
    };
  }, []);

  const user = useMemo<KeycloakUser | undefined>(() => {
    if (!authenticated || !keycloak.tokenParsed) return undefined;
    const parsed = keycloak.tokenParsed as Record<string, unknown> & {
      sub?: string;
      preferred_username?: string;
      email?: string;
      name?: string;
      realm_access?: { roles?: string[] };
    };
    return {
      sub: parsed.sub ?? "",
      username: parsed.preferred_username,
      email: parsed.email,
      name: parsed.name,
      roles: parsed.realm_access?.roles ?? [],
    };
  }, [authenticated]);

  const value = useMemo<KeycloakContextValue>(
    () => ({ initialized, authenticated, user, initError, login, logout }),
    [initialized, authenticated, user, initError],
  );

  return <KeycloakContext.Provider value={value}>{children}</KeycloakContext.Provider>;
}

export function useKeycloakContext(): KeycloakContextValue {
  const ctx = useContext(KeycloakContext);
  if (!ctx) throw new Error("useKeycloakContext must be used within <KeycloakProvider>");
  return ctx;
}
